import { useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import CommercialNotice from "../components/CommercialNotice";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Sparkles, Lock, ArrowUpRight, RefreshCw, AlertCircle, FileCheck, MessageSquare, ListChecks, DollarSign, Briefcase } from "lucide-react";

export default function DealAssistant() {
  const { user } = useAuth();
  const plan = (user?.plan || "free").toLowerCase();
  const isPro = plan === "pro" || plan === "agency";

  const [form, setForm] = useState({
    brand_name: "", deliverable: "", duration_weeks: "", exclusivity: false, notes: "",
  });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.brand_name.trim() || !form.deliverable.trim()) {
      setErr("Brand name and deliverable are required."); return;
    }
    setBusy(true); setErr(""); setReport(null);
    try {
      const payload = {
        brand_name: form.brand_name.trim(),
        deliverable: form.deliverable.trim(),
        duration_weeks: form.duration_weeks ? parseInt(form.duration_weeks, 10) : null,
        exclusivity: form.exclusivity,
        notes: form.notes || null,
      };
      const { data } = await api.post("/ai/deal-assistant", payload, { timeout: 180000 });
      setReport(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || (e.code === "ECONNABORTED" ? "Generation took too long — try again." : e.message));
    } finally { setBusy(false); }
  };

  if (!isPro) return (
    <DashboardLayout>
      <div className="p-6 md:p-12 max-w-5xl" data-testid="deal-paywall">
        <div className="label-eyebrow">Pro feature</div>
        <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tighter mt-2 leading-[0.95]">
          Don't leave money <span className="text-volt">on the table.</span>
        </h1>
        <p className="text-ink-muted text-lg mt-4 max-w-2xl">
          Most athletes accept the first number a brand offers — and lose 30-60% of fair value. AI Deal Assistant gives you the agent advice you couldn't afford: pricing benchmarks, ready-to-send negotiation phrases, red-flag detection, and the contract clauses you must insist on.
        </p>
        <div className="surface p-8 mt-10 border-volt/40 bg-volt/5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-volt text-black flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-2xl uppercase tracking-tight">Unlock with Pro</h2>
              <Link to="/app/pricing" className="btn-volt inline-flex items-center gap-2 mt-4" data-testid="deal-upgrade-btn">
                Upgrade to Pro <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
        <CommercialNotice />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12 max-w-6xl" data-testid="deal-assistant-root">
        <div className="mb-8">
          <div className="label-eyebrow inline-flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-volt" /> AI Deal Assistant</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2">Don't get underpaid.</h1>
          <p className="text-ink-muted text-sm md:text-base mt-2 max-w-2xl">Give a brand and deliverable — get fair-market pricing, negotiation scripts, red flags, and the clauses your contract must include.</p>
        </div>

        {/* INPUT FORM */}
        <div className="surface p-6 mb-6" data-testid="deal-form">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="label-eyebrow text-[10px] block mb-1.5">Brand name *</label>
              <input value={form.brand_name} onChange={e => upd("brand_name", e.target.value)} placeholder="e.g. a UK performance brand"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white" data-testid="deal-brand-input" />
            </div>
            <div>
              <label className="label-eyebrow text-[10px] block mb-1.5">Deliverable *</label>
              <input value={form.deliverable} onChange={e => upd("deliverable", e.target.value)} placeholder="e.g. 1 Instagram reel + 3 stories"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white" data-testid="deal-deliverable-input" />
            </div>
            <div>
              <label className="label-eyebrow text-[10px] block mb-1.5">Campaign duration (weeks)</label>
              <input type="number" value={form.duration_weeks} onChange={e => upd("duration_weeks", e.target.value)} placeholder="optional"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white" data-testid="deal-duration-input" />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={form.exclusivity} onChange={e => upd("exclusivity", e.target.checked)} className="accent-volt w-4 h-4" data-testid="deal-exclusivity-input" />
                <span className="text-sm">Brand wants category exclusivity</span>
              </label>
            </div>
          </div>
          <div>
            <label className="label-eyebrow text-[10px] block mb-1.5">Notes (optional)</label>
            <textarea value={form.notes} onChange={e => upd("notes", e.target.value)} maxLength={400} rows={2}
              placeholder="Anything else about the offer or context..."
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" data-testid="deal-notes-input" />
          </div>
          {err && <div className="text-xs text-[#FF3B30] mt-3" data-testid="deal-err">{err}</div>}
          <div className="flex justify-end mt-5">
            <button onClick={generate} disabled={busy} className="btn-volt inline-flex items-center gap-2 disabled:opacity-50" data-testid="deal-generate-btn">
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Analysing…" : "Get my deal playbook"}
            </button>
          </div>
        </div>

        {busy && !report && (
          <div className="surface p-12 text-center" data-testid="deal-generating">
            <RefreshCw className="w-8 h-8 text-volt animate-spin mx-auto mb-4" />
            <div className="font-heading text-lg uppercase tracking-tight">Crunching the numbers…</div>
          </div>
        )}

        {report && <DealReport report={report} />}
      </div>
    </DashboardLayout>
  );
}

function DealReport({ report }) {
  const p = report.pricing || {};
  const cur = p.currency === "GBP" ? "£" : (p.currency || "£");
  return (
    <div className="space-y-6" data-testid="deal-report">
      {/* PRICING */}
      <div className="surface p-6 border-volt/40 bg-volt/5" data-testid="deal-pricing">
        <div className="label-eyebrow inline-flex items-center gap-2 mb-4"><DollarSign className="w-3.5 h-3.5" /> Fair-market pricing</div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <PriceTier label="Conservative" value={`${cur}${p.low?.toLocaleString()}`} tone="muted" testid="price-low" />
          <PriceTier label="Fair market" value={`${cur}${p.mid?.toLocaleString()}`} tone="bold" testid="price-mid" />
          <PriceTier label="With leverage" value={`${cur}${p.high?.toLocaleString()}`} tone="muted" testid="price-high" />
        </div>
        {p.reasoning && (
          <div className="border-t border-line pt-4 text-sm text-ink-muted leading-relaxed italic">{p.reasoning}</div>
        )}
      </div>

      {/* NEGOTIATION PHRASES */}
      {report.negotiation_phrases?.length > 0 && (
        <section data-testid="deal-phrases">
          <Heading icon={<MessageSquare className="w-3.5 h-3.5" />} title="Negotiation phrases" subtitle="Copy/paste — these are what to say, word for word" />
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            {report.negotiation_phrases.map((np, i) => (
              <div key={i} className="surface p-5" data-testid={`phrase-${i}`}>
                <div className="label-eyebrow text-[10px] text-volt mb-2">{np.situation}</div>
                <p className="text-sm leading-relaxed italic">"{np.phrase}"</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* RED FLAGS */}
      {report.red_flags?.length > 0 && (
        <section data-testid="deal-redflags">
          <Heading icon={<AlertCircle className="w-3.5 h-3.5 text-[#FF3B30]" />} title="Red flags to watch for" subtitle="Things in their contract that should make you pause" />
          <div className="surface mt-4 divide-y divide-line">
            {report.red_flags.map((r, i) => (
              <div key={i} className="p-5 flex gap-4" data-testid={`redflag-${i}`}>
                <div className="w-8 h-8 border border-[#FF3B30]/60 text-[#FF3B30] flex items-center justify-center flex-shrink-0"><AlertCircle className="w-3.5 h-3.5" /></div>
                <div>
                  <div className="font-heading text-sm uppercase tracking-tight">{r.flag}</div>
                  <div className="text-sm text-ink-muted leading-relaxed mt-1">{r.why_it_matters}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* MUST-HAVE CLAUSES */}
      {report.must_have_clauses?.length > 0 && (
        <section data-testid="deal-clauses">
          <Heading icon={<FileCheck className="w-3.5 h-3.5" />} title="Must-have contract clauses" subtitle="Insist on these — every time" />
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            {report.must_have_clauses.map((c, i) => (
              <div key={i} className="surface p-5" data-testid={`clause-${i}`}>
                <div className="flex items-start gap-3">
                  <FileCheck className="w-4 h-4 text-volt flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-heading text-sm uppercase tracking-tight">{c.clause}</div>
                    <div className="text-xs text-ink-muted leading-relaxed mt-1">{c.plain_english}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* NEXT STEPS */}
      {report.next_steps?.length > 0 && (
        <section data-testid="deal-nextsteps">
          <Heading icon={<ListChecks className="w-3.5 h-3.5" />} title="Your next steps" subtitle="Do these in order" />
          <ol className="surface mt-4 divide-y divide-line">
            {report.next_steps.map((s, i) => (
              <li key={i} className="p-5 flex gap-4" data-testid={`nextstep-${i}`}>
                <div className="w-7 h-7 bg-volt text-black flex items-center justify-center font-mono font-semibold text-xs flex-shrink-0">{i + 1}</div>
                <p className="text-sm leading-relaxed flex-1">{s}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="text-[10px] text-ink-dim font-mono uppercase tracking-widest text-center pt-4 border-t border-line">
        Generated by PITCHD Deal Assistant — not legal advice. Always have a lawyer review a final contract.
      </div>
    </div>
  );
}

function PriceTier({ label, value, tone, testid }) {
  const bold = tone === "bold";
  return (
    <div className={`p-4 border ${bold ? "border-volt bg-bg-elev" : "border-line bg-bg-surface"}`} data-testid={testid}>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`font-heading mt-2 ${bold ? "text-3xl md:text-4xl text-volt" : "text-2xl md:text-3xl text-ink-muted"}`}>{value}</div>
    </div>
  );
}

function Heading({ icon, title, subtitle }) {
  return (
    <div>
      <div className="label-eyebrow inline-flex items-center gap-2">{icon}{title}</div>
      <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-tighter mt-1">{title}</h2>
      {subtitle && <p className="text-sm text-ink-muted mt-1 italic">{subtitle}</p>}
    </div>
  );
}
